/***
 *
 *   PROJECT BRANDING HOOK
 *   The single definition of the public-project fetch, shared by the branded
 *   layouts and the views inside them.
 *
 *   Both /project/:name and /project/:name/magic need the project's identity
 *   before anything is rendered, and on the magic route that has to happen
 *   before the visitor types their email. The layout and the view therefore
 *   both want the same data — so the key, the fetcher and the not-found
 *   predicate live here once rather than being written twice and drifting.
 *
 *   Only ONE request goes out: the key and query function are identical, the
 *   QueryClientProvider sits above <App/>, and the layout mounts in the same
 *   commit as the view, so react-query dedupes.
 *
 **********/

import { useQuery } from 'react-query';
import { useApiContext } from 'app/api';

export function useProjectBrand(name) {
    const { callApi } = useApiContext();

    const query = useQuery(
        ['projectByName', name],
        () =>
            callApi({
                url: `/api/projectByName?name=${name}`,
                auth: false,
            }),
        { enabled: !!name },
    );

    const project = query.data?.data;

    return {
        project: project,
        // A hex, or null for the Valetify default. BrandScope treats both the
        // same way — null means "render the stock palette".
        brandColor: project?.brand?.color || null,
        isLoading: query.isLoading,
        // callApi swallows the server's 400 into { status: 400, data: [] }
        // rather than throwing, so a missing project has to be detected on the
        // status rather than on query.isError.
        notFound: query.isFetched && (query.data?.status !== 200 || !project),
    };
}
